/* Mail en AI (ADR 0029): wat NATS met de mail van een Greenvision-postvak doet en wat het
 * taalmodel daarvoor verbruikt. NATS levert tellingen aan (migratie 106); hier worden ze gelezen
 * en opgeteld. De client komt als argument binnen, zodat de toetsen hem kunnen nabootsen.
 * Alleen de super_admin krijgt rijen terug (RLS); voor ieder ander is alles leeg. */

import { tariefVoor, TARIEVEN, type Tarief } from './tarieven';

export interface MailAiClient {
  from(tabel: string): any;
}

export interface Stand {
  postvak: string;
  /** Wanneer NATS telde, en wanneer het CRM de telling kreeg. */
  gemeten_op: string;
  ontvangen_op: string;
  laatste_mail_op: string | null;
  laatste_oordeel_op: string | null;
  draden_beoordeeld: number;
  draden_ruis: number;
  draden_fout: number;
  draden_wachtrij: number;
  aan_zet_ik: number;
  aan_zet_zij: number;
  punten_toezegging: number;
  punten_wacht: number;
  punten_vraag: number;
  punten_te_laat: number;
}

export interface DagRij {
  postvak: string;
  dag: string;
  mail_in: number;
  mail_uit: number;
}

export interface AiRij {
  postvak: string;
  dag: string;
  functie: string;
  model: string;
  aanroepen: number;
  fouten: number;
  input_tokens: number;
  output_tokens: number;
}

export interface MailAi {
  standen: Stand[];
  dagen: DagRij[];
  ai: AiRij[];
}

export const FUNCTIE_LABEL: Record<string, string> = {
  'mail-oordeel': 'Draadoordeel',
  'promise-shadow': 'Schaduwscan toezeggingen',
};

/** Ouder dan dit en NATS levert niet meer aan (de aanlevering loopt elke 10 minuten). */
export const VERS_MINUTEN = 30;

/* ── dagen ────────────────────────────────────────────────────────── */

/** YYYY-MM-DD in de tijdzone van de browser; NATS telt per dag in Europe/Amsterdam. */
export const lokaleDag = (d: Date) => new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
const middag = (dag: string) => new Date(`${dag}T12:00:00`);
export const dagTerug = (dag: string, n: number) => lokaleDag(new Date(middag(dag).getTime() - n * 86_400_000));
export const eersteVanMaand = (dag: string) => `${dag.slice(0, 7)}-01`;
/** De eerste dag van de maand vóór die van `dag`. */
export const eersteVanVorigeMaand = (dag: string) => eersteVanMaand(dagTerug(eersteVanMaand(dag), 1));

/** Hele minuten sinds dit tijdstip; null als het er niet is. */
export function minutenGeleden(iso: string | null | undefined, nu: Date = new Date()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso.replace(' ', 'T'));
  return Number.isNaN(t) ? null : Math.max(0, Math.floor((nu.getTime() - t) / 60_000));
}

export function geleden(iso: string | null | undefined, nu: Date = new Date()): string {
  const m = minutenGeleden(iso, nu);
  if (m === null) return 'nog nooit';
  if (m < 1) return 'zojuist';
  if (m < 60) return `${m} min geleden`;
  if (m < 48 * 60) return `${Math.floor(m / 60)} uur geleden`;
  return `${Math.floor(m / 1440)} dagen geleden`;
}

/* ── lezen ────────────────────────────────────────────────────────── */

const STAND_KOLOMMEN =
  'postvak, gemeten_op, ontvangen_op, laatste_mail_op, laatste_oordeel_op, draden_beoordeeld, draden_ruis, draden_fout, draden_wachtrij, aan_zet_ik, aan_zet_zij, punten_toezegging, punten_wacht, punten_vraag, punten_te_laat';

/** Een leesfout is hier geen lege lijst: op een beheerpagina wil je weten dat het lezen niet lukte. */
async function lees<T>(q: PromiseLike<{ data: T[] | null; error: { message?: string } | null }>, wat: string): Promise<T[]> {
  const r = await q;
  if (r.error) throw new Error(`${wat} lezen lukte niet${r.error.message ? `: ${r.error.message}` : '.'}`);
  return r.data ?? [];
}

/** De stand per postvak, de maildagen van de laatste 35 dagen en de aanroepen vanaf de vorige maand. */
export async function haalMailAi(client: MailAiClient, nu: Date = new Date()): Promise<MailAi> {
  const vandaag = lokaleDag(nu);
  const [standen, dagen, ai] = await Promise.all([
    lees<Stand>(client.from('mail_status').select(STAND_KOLOMMEN).order('postvak'), 'De mailstatus'),
    lees<DagRij>(client.from('mail_status_dagen').select('postvak, dag, mail_in, mail_uit').gte('dag', dagTerug(vandaag, 34)).order('dag'), 'De maildagen'),
    lees<AiRij>(
      client.from('mail_ai_dagen').select('postvak, dag, functie, model, aanroepen, fouten, input_tokens, output_tokens')
        .gte('dag', eersteVanVorigeMaand(vandaag)).order('dag'),
      'De aanroepen',
    ),
  ]);
  /* bigint komt uit PostgREST als getal of als tekst; hier altijd een getal. */
  return { standen, dagen, ai: ai.map((a) => ({ ...a, input_tokens: Number(a.input_tokens) || 0, output_tokens: Number(a.output_tokens) || 0 })) };
}

/* ── optellen ─────────────────────────────────────────────────────── */

export interface Som {
  aanroepen: number;
  fouten: number;
  input_tokens: number;
  output_tokens: number;
  /** Geschat bedrag in dollars voor de aanroepen waarvan het tarief bekend is. */
  usd: number;
  /** Aanroepen van een model zonder tarief in tarieven.ts; die zitten niet in `usd`. */
  zonderTarief: number;
}

const LEEG: Som = { aanroepen: 0, fouten: 0, input_tokens: 0, output_tokens: 0, usd: 0, zonderTarief: 0 };

/** Wat één rij kostte, of null als het model geen tarief heeft. */
export function kostenUsd(r: Pick<AiRij, 'dag' | 'model' | 'input_tokens' | 'output_tokens'>, tarieven: Tarief[] = TARIEVEN): number | null {
  const t = tariefVoor(r.model, r.dag, tarieven);
  return t ? (r.input_tokens * t.inPerMiljoen + r.output_tokens * t.uitPerMiljoen) / 1_000_000 : null;
}

export function som(rijen: AiRij[], tarieven: Tarief[] = TARIEVEN): Som {
  return rijen.reduce<Som>((s, r) => {
    const k = kostenUsd(r, tarieven);
    return {
      aanroepen: s.aanroepen + r.aanroepen,
      fouten: s.fouten + r.fouten,
      input_tokens: s.input_tokens + r.input_tokens,
      output_tokens: s.output_tokens + r.output_tokens,
      usd: s.usd + (k ?? 0),
      zonderTarief: s.zonderTarief + (k === null ? r.aanroepen : 0),
    };
  }, LEEG);
}

/** Rijen van `vanaf` tot en met `tot` (beide YYYY-MM-DD). */
export const tussen = <T extends { dag: string }>(rijen: T[], vanaf: string, tot: string) => rijen.filter((r) => r.dag >= vanaf && r.dag <= tot);

export interface DagRegel extends Som {
  dag: string;
  mail_in: number;
  mail_uit: number;
}

/** De laatste `aantal` dagen, vandaag bovenaan; een dag zonder mail en zonder aanroepen staat er met nullen. */
export function perDag(m: Pick<MailAi, 'dagen' | 'ai'>, nu: Date = new Date(), aantal = 14, tarieven: Tarief[] = TARIEVEN): DagRegel[] {
  const vandaag = lokaleDag(nu);
  return Array.from({ length: aantal }, (_, i) => {
    const dag = dagTerug(vandaag, i);
    const mail = m.dagen.filter((d) => d.dag === dag);
    return {
      dag,
      mail_in: mail.reduce((t, d) => t + d.mail_in, 0),
      mail_uit: mail.reduce((t, d) => t + d.mail_uit, 0),
      ...som(m.ai.filter((a) => a.dag === dag), tarieven),
    };
  });
}

export interface FunctieRegel extends Som {
  functie: string;
  model: string;
}

/** Per functie en model, de duurste bovenaan. */
export function perFunctie(ai: AiRij[], tarieven: Tarief[] = TARIEVEN): FunctieRegel[] {
  const groepen = new Map<string, AiRij[]>();
  for (const a of ai) {
    const sleutel = `${a.functie}|${a.model}`;
    if (!groepen.has(sleutel)) groepen.set(sleutel, []);
    groepen.get(sleutel)!.push(a);
  }
  return [...groepen.values()]
    .map((rijen) => ({ functie: rijen[0]!.functie, model: rijen[0]!.model, ...som(rijen, tarieven) }))
    .sort((a, b) => b.usd - a.usd || b.aanroepen - a.aanroepen || a.functie.localeCompare(b.functie));
}

export interface Overzicht {
  vandaag: Som;
  dezeMaand: Som;
  vorigeMaand: Som;
  /** Mislukte aanroepen in de laatste zeven dagen. */
  foutenWeek: number;
  wachtrij: number;
  /** De oudste aanlevering onder de postvakken; null als er nog niets is aangeleverd. */
  ontvangenOp: string | null;
  vers: boolean;
}

export function overzicht(m: MailAi, nu: Date = new Date(), tarieven: Tarief[] = TARIEVEN): Overzicht {
  const vandaag = lokaleDag(nu);
  const maand = eersteVanMaand(vandaag);
  const vorige = eersteVanVorigeMaand(vandaag);
  const ontvangenOp = m.standen.length ? m.standen.map((s) => s.ontvangen_op).sort()[0]! : null;
  const minuten = minutenGeleden(ontvangenOp, nu);
  return {
    vandaag: som(tussen(m.ai, vandaag, vandaag), tarieven),
    dezeMaand: som(tussen(m.ai, maand, vandaag), tarieven),
    vorigeMaand: som(tussen(m.ai, vorige, dagTerug(maand, 1)), tarieven),
    foutenWeek: som(tussen(m.ai, dagTerug(vandaag, 6), vandaag), tarieven).fouten,
    wachtrij: m.standen.reduce((t, s) => t + s.draden_wachtrij, 0),
    ontvangenOp,
    vers: minuten !== null && minuten <= VERS_MINUTEN,
  };
}
